import React, { FormEvent, ReactNode, useState } from "react";
import {
  Button,
  Form,
  FormField,
  Input,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3/polaris";
import { PinType } from "../lib/pin-presets";

interface PinFormProps {
  error: string;
  onSubmit: (pin: string) => Promise<boolean>;
  type: PinType;
  pinRegex: RegExp;
  invalidPinMessage: string;
  pinDescription: string;
  inputMode?: "text" | "numeric";
  infoText: ReactNode;
  isLoading?: boolean;
}

interface PinFormState {
  pin: string;
  confirmPin: string;
  hasSubmittedAtLeastOnce: boolean;
}

const initialFormState: PinFormState = {
  pin: "",
  confirmPin: "",
  hasSubmittedAtLeastOnce: false,
};

const validatePin = ({
  pin,
  regex,
  invalidMessage,
}: {
  pin: string;
  regex: RegExp;
  invalidMessage: string;
}) => {
  if (pin.length === 0) return "PIN is required.";
  if (!regex.test(pin)) return invalidMessage;
  return "";
};

const validateConfirmPin = ({
  confirmPin,
  pin,
}: {
  confirmPin: string;
  pin: string;
}) => {
  if (confirmPin.length === 0) return "Please confirm your PIN.";
  if (confirmPin !== pin) return "PINs do not match.";
  return "";
};

export const PinForm = ({
  type: pinType,
  error,
  onSubmit,
  pinRegex,
  invalidPinMessage,
  pinDescription,
  inputMode = "text",
  infoText,
  isLoading = false,
}: PinFormProps) => {
  const [formState, setFormState] = useState(initialFormState);

  const pinError = validatePin({
    pin: formState.pin,
    regex: pinRegex,
    invalidMessage: invalidPinMessage,
  });
  const confirmPinError = validateConfirmPin({
    confirmPin: formState.confirmPin,
    pin: formState.pin,
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pinError || confirmPinError) {
      setFormState((prev) => ({ ...prev, hasSubmittedAtLeastOnce: true }));
      return;
    }
    const ok = await onSubmit(formState.pin);
    if (ok) {
      setFormState(initialFormState);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Form
        actions={
          <Button
            variant="primary"
            loading={isLoading}
            disabled={isLoading}
            data-testid="set-button"
          >
            Set PIN
          </Button>
        }
        errorText={error}
        data-testid="set-pin-form"
      >
        <SpaceBetween direction="vertical" size="s">
          <p data-testid="form-info">
            You are setting your Frontier {pinType} PIN. {infoText}
          </p>
          <FormField
            label="New PIN"
            errorText={formState.hasSubmittedAtLeastOnce ? pinError : ""}
            data-testid="new-pin-form-field"
            description={pinDescription}
          >
            <Input
              type="password"
              inputMode={inputMode}
              autoComplete="new-password"
              value={formState.pin}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  pin: e.detail.value.trim(),
                }))
              }
              data-testid="new-pin-input"
            />
          </FormField>
          <FormField
            label="Confirm PIN"
            errorText={formState.hasSubmittedAtLeastOnce ? confirmPinError : ""}
            data-testid="confirm-pin-form-field"
            description="Enter the same PIN as above."
          >
            <Input
              type="password"
              inputMode={inputMode}
              autoComplete="new-password"
              value={formState.confirmPin}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  confirmPin: e.detail.value.trim(),
                }))
              }
              data-testid="confirm-pin-input"
            />
          </FormField>
        </SpaceBetween>
      </Form>
    </form>
  );
};
